import React, { useState, useMemo } from 'react';
import {
    Box,
    Button,
    Group,
    Text,
    TextInput,
    Select,
    ActionIcon,
    Modal,
    Paper,
    NumberInput,
    useMantineTheme
} from '@mantine/core';
import { IconPlus, IconDots, IconTrash } from '@tabler/icons-react';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';

import PartSearchDialog from './PartSearchDialog'; // Assuming this is the part search dialog component 

const LineGrid = () => {
    const theme = useMantineTheme();
    const [lines, setLines] = useState([
        {
            id: 1,
            line: 1,
            print: true,
            type: 'S',
            sts: 'O',
            partNumber: '',
            description: '',
            qtyReq: 1,
            um: '',
            unitPrice: 0,
            umPrice: '',
            qtyBid: 0,
            cond: '',
            dly: '0 Days',
            rng: '',
            total: 0
        }
    ]);

    const [partSearchOpen, setPartSearchOpen] = useState(false);
    const [selectedLineId, setSelectedLineId] = useState(null);

    const handlePartSearch = (lineId) => {
        setSelectedLineId(lineId);
        setPartSearchOpen(true);
    };

    const addNewLine = () => {
        const newLine = {
            id: Date.now(),
            line: lines.length + 1,
            print: true,
            type: 'S',
            sts: 'O',
            partNumber: '',
            description: '',
            qtyReq: 1,
            um: '',
            unitPrice: 0,
            umPrice: '',
            qtyBid: 0,
            cond: '',
            dly: '0 Days',
            rng: '',
            total: 0
        };
        setLines([...lines, newLine]);
    };

    const deleteLine = (lineId) => {
        if (lines.length > 1) {
            setLines(lines.filter(line => line.id !== lineId));
        }
    };

    const updateLine = (lineId, field, value) => {
        setLines(lines.map(line => {
            if (line.id === lineId) {
                const updated = { ...line, [field]: value };

                // Calculate total when quantity or price changes
                if (field === 'qtyReq' || field === 'unitPrice') {
                    updated.total = (updated.qtyReq || 0) * (updated.unitPrice || 0);
                }

                return updated;
            }
            return line;
        }));
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'actions',
            header: '',
            size: 40,
            enableSorting: false,
            Cell: ({ row }) => (
                <Group spacing={4}>
                    <ActionIcon
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={() => deleteLine(row.original.id)}
                        disabled={lines.length <= 1}
                    >
                        <IconTrash size={12} />
                    </ActionIcon>
                </Group>
            ),
        },
        {
            accessorKey: 'line',
            header: 'Line',
            size: 60,
            Cell: ({ row }) => (
                <Text size="xs" ta="center">{row.original.line}</Text>
            ),
        },
        {
            accessorKey: 'print',
            header: 'Print',
            size: 60,
            Cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={row.original.print}
                    onChange={(e) => updateLine(row.original.id, 'print', e.target.checked)}
                    style={{ margin: 'auto', display: 'block' }}
                />
            ),
        },
        {
            accessorKey: 'type',
            header: 'Type',
            size: 80,
            Cell: ({ row }) => (
                <Select
                    size="xs"
                    value={row.original.type}
                    onChange={(value) => updateLine(row.original.id, 'type', value)}
                    data={['S', 'M', 'L', 'C']}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px' } }}
                />
            ),
        },
        {
            accessorKey: 'sts',
            header: 'Sts',
            size: 60,
            Cell: ({ row }) => (
                <Select
                    size="xs"
                    value={row.original.sts}
                    onChange={(value) => updateLine(row.original.id, 'sts', value)}
                    data={['O', 'C', 'H', 'X']}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px' } }}
                />
            ),
        },
        {
            accessorKey: 'partNumber',
            header: 'P/N',
            size: 120,
            Cell: ({ row }) => (
                <Group spacing={2}>
                    <TextInput
                        size="xs"
                        value={row.original.partNumber}
                        onChange={(e) => updateLine(row.original.id, 'partNumber', e.target.value)}
                        styles={{
                            input: {
                                fontSize: '11px',
                                minHeight: '20px',
                                height: '20px',
                                flex: 1
                            }
                        }}
                        style={{ flex: 1 }}
                    />
                    <ActionIcon
                        size="xs"
                        variant="subtle"
                        onClick={() => handlePartSearch(row.original.id)}
                    >
                        <IconDots size={12} />
                    </ActionIcon>
                </Group>
            ),
        },
        {
            accessorKey: 'description',
            header: 'Description',
            size: 200,
            Cell: ({ row }) => (
                <TextInput
                    size="xs"
                    value={row.original.description}
                    onChange={(e) => updateLine(row.original.id, 'description', e.target.value)}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px' } }}
                />
            ),
        },
        {
            accessorKey: 'qtyReq',
            header: 'Q. Req',
            size: 80,
            Cell: ({ row }) => (
                <NumberInput
                    size="xs"
                    value={row.original.qtyReq}
                    onChange={(value) => updateLine(row.original.id, 'qtyReq', value)}
                    min={0}
                    precision={0}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px', textAlign: 'right' } }}
                />
            ),
        },
        {
            accessorKey: 'um',
            header: 'UM',
            size: 60,
            Cell: ({ row }) => (
                <TextInput
                    size="xs"
                    value={row.original.um}
                    onChange={(e) => updateLine(row.original.id, 'um', e.target.value)}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px' } }}
                />
            ),
        },
        {
            accessorKey: 'unitPrice',
            header: 'Unit Price',
            size: 100,
            Cell: ({ row }) => (
                <NumberInput
                    size="xs"
                    value={row.original.unitPrice}
                    onChange={(value) => updateLine(row.original.id, 'unitPrice', value)}
                    min={0}
                    precision={2}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px', textAlign: 'right' } }}
                />
            ),
        },
        {
            accessorKey: 'umPrice',
            header: 'UM',
            size: 60,
            Cell: ({ row }) => (
                <TextInput
                    size="xs"
                    value={row.original.umPrice}
                    onChange={(e) => updateLine(row.original.id, 'umPrice', e.target.value)}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px' } }}
                />
            ),
        },
        {
            accessorKey: 'qtyBid',
            header: 'Q. Bid',
            size: 80,
            Cell: ({ row }) => (
                <NumberInput
                    size="xs"
                    value={row.original.qtyBid}
                    onChange={(value) => updateLine(row.original.id, 'qtyBid', value)}
                    min={0}
                    precision={0}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px', textAlign: 'right' } }}
                />
            ),
        },
        {
            accessorKey: 'cond',
            header: 'Cond',
            size: 80,
            Cell: ({ row }) => (
                <TextInput
                    size="xs"
                    value={row.original.cond}
                    onChange={(e) => updateLine(row.original.id, 'cond', e.target.value)}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px' } }}
                />
            ),
        },
        {
            accessorKey: 'dly',
            header: 'Dly',
            size: 80,
            Cell: ({ row }) => (
                <TextInput
                    size="xs"
                    value={row.original.dly}
                    onChange={(e) => updateLine(row.original.id, 'dly', e.target.value)}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px' } }}
                />
            ),
        },
        {
            accessorKey: 'rng',
            header: 'Rng',
            size: 80,
            Cell: ({ row }) => (
                <TextInput
                    size="xs"
                    value={row.original.rng}
                    onChange={(e) => updateLine(row.original.id, 'rng', e.target.value)}
                    styles={{ input: { fontSize: '11px', minHeight: '20px', height: '20px' } }}
                />
            ),
        },
        {
            accessorKey: 'total',
            header: 'Total',
            size: 100,
            Cell: ({ row }) => (
                <Text size="xs" ta="right" style={{ padding: '4px 8px' }}>
                    {(row.original.total || 0).toFixed(2)}
                </Text>
            ),
        },
    ], [lines]);

    const table = useMantineReactTable({
        columns,
        data: lines,
        enableRowSelection: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enablePagination: false,
        enableSorting: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        getRowId: (row) => row.id.toString(),
        mantineTableProps: {
            withBorder: true,
            fontSize: 'xs',
            style: { fontSize: '11px' }
        },
        mantineTableHeadProps: {
            style: {
                fontSize: '11px',
          
            }
        },
        mantineTableBodyCellProps: {
            style: { padding: '2px 4px' }
        },
        initialState: {
            density: 'xs',
        },
    });

    const calculateSubTotal = () => {
        return lines.reduce((sum, line) => sum + (line.total || 0), 0);
    };

    return (
        <Box>
            <Paper withBorder p="xs">
                <Group position="apart" mb="xs">
                    <Text size="sm" weight={500}>Quote Lines</Text>
                    <Button
                        size="xs"
                        leftIcon={<IconPlus size={14} />}
                        onClick={addNewLine}
                        variant="filled"
                    >
                        Add Line
                    </Button>
                </Group>

                <Box style={{ minHeight: '300px' }}>
                    <MantineReactTable table={table} />
                </Box>

                {/* Summary Row */}
                <Group position="right" mt="xs" spacing="lg">
                    <Text size="sm" weight={500}>
                        Subtotal: ${calculateSubTotal().toFixed(2)}
                    </Text>
                </Group>
            </Paper>

             <PartSearchDialog
                opened={partSearchOpen}
                onClose={() => setPartSearchOpen(false)}
                onPartSelect={(part) => {
                    // Update the line with selected part
                    updateLine(selectedLineId, 'partNumber', part.partnumber);
                    updateLine(selectedLineId, 'description', part.description);
                    setPartSearchOpen(false);
                }}
            />
        </Box>
    );
};

export default LineGrid;